import { createContext, type Dispatch, type SetStateAction } from "react";
import { UrlUpdateType } from "use-query-params";
import { Bill, BillSortValue } from "@common/types/bill";
import type { LoadMoreTableState } from "@hooks/useLoadMoreTableState";
import { UseSort } from "./useSort";

export interface PaginatedBillsTableContextValue {
    currentPage: number;
    totalPages: number;
    billCount: number;
    setPage:
        | Dispatch<SetStateAction<number>>
        | ((
              newValue: number | null | undefined,
              updateType?: UrlUpdateType,
          ) => void);
    handleSortChange: UseSort["handleSortChange"];
    currentSort: UseSort["currentSort"];
    bills?: Bill[];
    isLoading: boolean;
    /** Set when the table loads more on scroll instead of paging. */
    loadMore?: LoadMoreTableState;
}

export const BILLS_TABLE_PAGE_SIZE = 25;

interface BillsPage {
    pageCount: number;
    total: number;
    data: Bill[];
}

interface ToPaginatedBillsTableContextValueParams extends Omit<
    PaginatedBillsTableContextValue,
    "currentPage" | "totalPages" | "billCount" | "bills"
> {
    page: number;
    billsPage: BillsPage | undefined;
}

export const toPaginatedBillsTableContextValue = ({
    page,
    billsPage,
    ...rest
}: ToPaginatedBillsTableContextValueParams): PaginatedBillsTableContextValue => ({
    currentPage: page,
    totalPages: billsPage?.pageCount ?? 1,
    billCount: billsPage?.total ?? 0,
    bills: billsPage?.data,
    ...rest,
});

const defaultValue: PaginatedBillsTableContextValue = {
    currentPage: 0,
    totalPages: 0,
    billCount: 0,
    setPage: () => {},
    handleSortChange: () => {},
    currentSort: BillSortValue.NONE,
    isLoading: false,
};

export const PaginatedBillsTableContext =
    createContext<PaginatedBillsTableContextValue>(defaultValue);
